import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X } from "lucide-react";
import RichTextEditor from "@/components/common/RichTextEditor";
import FileUpload from "@/components/common/FileUpload";

/**
 * Generic resource manager for admin CMS.
 * Props:
 *  - title: page title
 *  - endpoint: base path (e.g. '/news', '/admin/news')
 *  - listEndpoint: GET path (defaults to public list)
 *  - fields: [{ name, label, type, required, options?, placeholder? }]
 *  - listColumns: [{ key, label, render? }]
 *  - idField: default "id"
 *  - onListParams: default {}
 */
export default function ResourceManager({
  title,
  description,
  publicList,           // GET all list (public endpoint)
  adminBase,            // admin POST/PATCH/DELETE base (e.g. /admin/news)
  fields,
  listColumns,
  idField = "id",
  emptyText = "Belum ada data.",
  listParams = {},
  extraRowActions = null,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null=none, {}=new, obj=edit
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(publicList, { params: listParams })
      .then((r) => setItems(r.data.items || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  const openNew = () => {
    const empty = {};
    fields.forEach((f) => { empty[f.name] = f.default ?? (f.type === "boolean" ? false : f.type === "list" ? "" : ""); });
    setForm(empty);
    setEditing({});
  };

  const openEdit = (item) => {
    const f = { ...item };
    fields.forEach((fld) => {
      if (fld.type === "list" && Array.isArray(f[fld.name])) f[fld.name] = f[fld.name].join("\n");
    });
    setForm(f);
    setEditing(item);
  };

  const close = () => { setEditing(null); setForm({}); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      fields.forEach((f) => {
        if (f.type === "list") {
          payload[f.name] = String(payload[f.name] || "").split("\n").map(s => s.trim()).filter(Boolean);
        }
        if (f.type === "number") payload[f.name] = payload[f.name] === "" || payload[f.name] == null ? 0 : Number(payload[f.name]);
        if (f.type === "boolean") payload[f.name] = Boolean(payload[f.name]);
      });
      if (editing && editing[idField]) {
        await api.patch(`${adminBase}/${editing[idField]}`, payload);
        toast.success("Berhasil diperbarui");
      } else {
        await api.post(adminBase, payload);
        toast.success("Berhasil dibuat");
      }
      close();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!confirm(`Hapus "${item.title || item.name}"?`)) return;
    try {
      await api.delete(`${adminBase}/${item[idField]}`);
      toast.success("Berhasil dihapus");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid={`admin-resource-${title.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{title}</h1>
          {description && <p className="text-slate-600 mt-1">{description}</p>}
        </div>
        <button onClick={openNew} className="btn-primary" data-testid="admin-add-new">
          <Plus className="h-4 w-4" /> Tambah Baru
        </button>
      </div>

      <div className="card-sf overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {listColumns.map((c) => (
                <th key={c.key} className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">{c.label}</th>
              ))}
              <th className="text-right px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={listColumns.length + 1} className="p-8 text-center text-slate-500">Memuat...</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={listColumns.length + 1} className="p-8 text-center text-slate-500">{emptyText}</td></tr>
            )}
            {items.map((it) => (
              <tr key={it[idField]} className="border-b border-slate-100 hover:bg-slate-50">
                {listColumns.map((c) => (
                  <td key={c.key} className="px-5 py-3 text-sm text-slate-700">
                    {c.render ? c.render(it) : String(it[c.key] ?? "")}
                  </td>
                ))}
                <td className="px-5 py-3 text-right">
                  <div className="inline-flex gap-2">
                    {extraRowActions && extraRowActions(it)}
                    <button onClick={() => openEdit(it)} className="h-8 w-8 rounded-lg border border-slate-200 hover:border-[var(--brand-blue)] hover:text-[var(--brand-blue)] flex items-center justify-center" data-testid={`admin-edit-${it[idField]}`}>
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => remove(it)} className="h-8 w-8 rounded-lg border border-slate-200 hover:border-red-500 hover:text-red-500 flex items-center justify-center" data-testid={`admin-delete-${it[idField]}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing !== null && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-start justify-center p-4 overflow-y-auto py-10" onClick={close}>
          <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="admin-form-modal">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">{editing[idField] ? `Edit ${title}` : `Tambah ${title}`}</h2>
              <button onClick={close} className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center"><X className="h-4 w-4" /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              {fields.map((f) => (
                <FormField key={f.name} field={f} value={form[f.name]} onChange={(v) => setForm({ ...form, [f.name]: v })} />
              ))}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                <button type="button" onClick={close} className="btn-ghost">Batal</button>
                <button type="submit" disabled={saving} className="btn-secondary" data-testid="admin-form-save">
                  {saving ? "Menyimpan..." : "Simpan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function FormField({ field, value, onChange }) {
  const base = "w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[var(--brand-blue)]";
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-700 mb-1.5">
        {field.label}{field.required && <span className="text-[var(--brand-orange)]"> *</span>}
      </label>
      {field.type === "richtext" ? (
        <RichTextEditor value={value ?? ""} onChange={onChange} placeholder={field.placeholder} />
      ) : field.type === "image" || field.type === "file" ? (
        <FileUpload
          value={value || ""}
          onChange={onChange}
          accept={field.type === "file" ? (field.accept || "application/pdf") : "image/*"}
          label={field.uploadLabel || (field.type === "file" ? "Upload File" : "Upload Gambar")}
        />
      ) : field.type === "textarea" || field.type === "list" ? (
        <textarea
          rows={field.rows || 4}
          value={value ?? ""}
          required={field.required}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className={base}
        />
      ) : field.type === "select" ? (
        <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={base} required={field.required}>
          {(field.options || []).map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
        </select>
      ) : field.type === "boolean" ? (
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4" />
          <span className="text-sm text-slate-700">{field.checkboxLabel || "Aktif"}</span>
        </label>
      ) : (
        <input
          type={field.type || "text"}
          value={value ?? ""}
          required={field.required}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className={base}
        />
      )}
      {field.help && <p className="mt-1 text-xs text-slate-500">{field.help}</p>}
    </div>
  );
}
